import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowDown, ArrowLeft, ArrowUp, Eye, EyeOff, RotateCcw, Send } from 'lucide-react';
import { AvatarCircle, Button, PackIconGlyph, cn } from '@jeraisonne/ui';
import {
  SCHOOL_MONTHS,
  levelLabel,
  masteryTone,
  monthLongLabel,
  monthShortLabel,
  packPeriodLabel,
  movePack,
  packMasteryPercent,
  subjectLabel,
  type ClassPackOverride,
  type MasteryTone,
} from '@jeraisonne/db/curriculum';
import {
  useAssignPack,
  useClassProgression,
  useResetClassProgression,
  useSetClassProgression,
  type ClassProgressionPack,
} from '@/lib/curriculum/api';

const TONE_CLASSES: Record<MasteryTone, string> = {
  empty: 'bg-ink-50 text-ink-300',
  none: 'bg-ink-100 text-ink-500',
  low: 'bg-amber-100 text-amber-800',
  mid: 'bg-sky-100 text-sky-800',
  high: 'bg-emerald-100 text-emerald-800',
};

/** Période indicative du pack ; si la classe a déplacé son début, seul le mois choisi. */
function periodOf(p: ClassProgressionPack): string {
  return p.start_month === p.default_start_month
    ? packPeriodLabel(p.start_month, p.end_month)
    : monthShortLabel(p.start_month);
}

function toOverrides(packs: ClassProgressionPack[]): ClassPackOverride[] {
  return packs.map((p, i) => ({
    pack_id: p.id,
    position: i,
    start_month: p.start_month,
    hidden: p.hidden,
  }));
}

/**
 * Route prof `/tutor/classes/$classId/progression` (lot F, migration 0289) :
 * la progression type du niveau de la classe, RÉORDONNABLE (ordre, mois,
 * masquer) pour cette classe, une grille élèves × packs colorée par maîtrise
 * (cartes ≥ 80 %), et « Assigner le pack » qui ajoute toutes ses SKILLCARDS à
 * chaque élève (une seule notification regroupée par élève, file 0284).
 */
export function TutorClassProgressionPage() {
  const { classId } = useParams({ strict: false }) as { classId: string };
  const progression = useClassProgression(classId);
  const save = useSetClassProgression(classId);
  const reset = useResetClassProgression(classId);
  const assign = useAssignPack(classId);
  const [notice, setNotice] = useState<string | null>(null);
  const data = progression.data;
  const packs = data?.packs ?? [];
  const visible = packs.filter((p) => !p.hidden);
  const anyOverride = packs.some((p) => p.overridden);

  const persist = (items: ClassPackOverride[]) => {
    setNotice(null);
    save.mutate(items);
  };
  const setMonth = (index: number, month: number) =>
    persist(toOverrides(packs.map((p, i) => (i === index ? { ...p, start_month: month } : p))));
  const toggleHidden = (index: number) =>
    persist(toOverrides(packs.map((p, i) => (i === index ? { ...p, hidden: !p.hidden } : p))));
  const onAssign = async (pack: ClassProgressionPack) => {
    setNotice(null);
    try {
      const n = await assign.mutateAsync(pack.id);
      setNotice(
        n === 0
          ? `Toutes les SKILLCARDS de « ${pack.title} » étaient déjà assignées.`
          : `${n} SKILLCARD${n > 1 ? 'S' : ''} ajoutée${n > 1 ? 's' : ''} aux élèves (« ${pack.title} »). Ils reçoivent une notification regroupée.`,
      );
    } catch (e) {
      setNotice(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6 md:p-8">
      <Link
        to="/tutor/classes/$classId"
        params={{ classId }}
        className="font-display text-ink-500 hover:text-ink-900 inline-flex items-center gap-1.5 text-sm font-semibold"
      >
        <ArrowLeft className="h-4 w-4" /> Retour à la classe
      </Link>

      <div>
        <h1 className="font-display text-ink-900 text-2xl font-extrabold">
          Progression de l’année{data ? ` · ${data.class.name}` : ''}
        </h1>
        {data && (
          <p className="text-ink-500 text-sm">
            Progression type JeRaisonne · {levelLabel(data.class.level)}
            {data.class.subject ? ` · ${subjectLabel(data.class.subject)}` : ''}. Réordonne les
            packs pour suivre TA progression : tes élèves voient leur maîtrise pack par pack.
          </p>
        )}
      </div>

      {progression.error && (
        <p role="alert" className="text-accent-600 text-sm">
          {progression.error.message.includes('not_authorized')
            ? 'Seul le prof de la classe peut voir sa progression.'
            : 'Impossible de charger la progression.'}
        </p>
      )}
      {data && packs.length === 0 && (
        <p className="text-ink-600 text-sm">
          Aucun pack publié pour ce niveau et cette matière pour l’instant.
        </p>
      )}
      {notice && (
        <p role="status" className="bg-primary-50 text-primary-800 rounded-xl px-3 py-2 text-sm">
          {notice}
        </p>
      )}
      {save.error && (
        <p role="alert" className="text-accent-600 text-sm">
          {save.error.message}
        </p>
      )}

      {packs.length > 0 && (
        <section aria-labelledby="packs-title" className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 id="packs-title" className="font-display text-ink-900 text-lg font-bold">
              Ordre des packs
            </h2>
            {anyOverride && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={reset.isPending}
                onClick={() => reset.mutate()}
              >
                <RotateCcw className="h-4 w-4" /> Revenir à la progression type
              </Button>
            )}
          </div>
          <ol className="divide-ink-100 border-ink-200 divide-y rounded-2xl border bg-white">
            {packs.map((pack, i) => (
              <li
                key={pack.id}
                className={cn(
                  'flex flex-wrap items-center gap-2 px-3 py-2',
                  pack.hidden && 'opacity-50',
                )}
              >
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                  style={{
                    background: `${pack.color ?? '#4DA3FF'}22`,
                    color: pack.color ?? '#4DA3FF',
                  }}
                >
                  <PackIconGlyph icon={pack.icon} className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-ink-900 truncate text-sm font-semibold">{pack.title}</p>
                  <p className="text-ink-500 text-xs">
                    {periodOf(pack)} · {pack.card_count} SKILLCARD{pack.card_count > 1 ? 'S' : ''}
                  </p>
                </div>
                <label className="sr-only" htmlFor={`month-${pack.id}`}>
                  Mois de début de {pack.title}
                </label>
                <select
                  id={`month-${pack.id}`}
                  value={pack.start_month}
                  disabled={save.isPending}
                  onChange={(e) => setMonth(i, Number(e.target.value))}
                  className="border-ink-200 h-11 rounded-lg border bg-white px-2 text-sm"
                >
                  {SCHOOL_MONTHS.map((m) => (
                    <option key={m} value={m}>
                      {monthLongLabel(m)}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  aria-label={`Monter ${pack.title}`}
                  disabled={i === 0 || save.isPending}
                  onClick={() => persist(movePack(packs, i, -1))}
                  className="text-ink-500 hover:bg-ink-50 flex h-11 w-11 items-center justify-center rounded-lg disabled:opacity-30"
                >
                  <ArrowUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  aria-label={`Descendre ${pack.title}`}
                  disabled={i === packs.length - 1 || save.isPending}
                  onClick={() => persist(movePack(packs, i, 1))}
                  className="text-ink-500 hover:bg-ink-50 flex h-11 w-11 items-center justify-center rounded-lg disabled:opacity-30"
                >
                  <ArrowDown className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  aria-label={pack.hidden ? `Afficher ${pack.title}` : `Masquer ${pack.title}`}
                  disabled={save.isPending}
                  onClick={() => toggleHidden(i)}
                  className="text-ink-500 hover:bg-ink-50 flex h-11 w-11 items-center justify-center rounded-lg"
                >
                  {pack.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <Button
                  type="button"
                  size="sm"
                  disabled={assign.isPending || pack.card_count === 0 || pack.hidden}
                  onClick={() => void onAssign(pack)}
                >
                  <Send className="h-4 w-4" /> Assigner le pack
                </Button>
              </li>
            ))}
          </ol>
        </section>
      )}

      {data && data.students.length > 0 && visible.length > 0 && (
        <section aria-labelledby="grid-title" className="space-y-2">
          <h2 id="grid-title" className="font-display text-ink-900 text-lg font-bold">
            Maîtrise des élèves
          </h2>
          <p className="text-ink-500 text-xs">
            Cartes maîtrisées (≥ 80 %) sur le nombre de cartes du pack. Vert : pack maîtrisé.
          </p>
          <div className="border-ink-200 overflow-x-auto rounded-2xl border bg-white">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="text-ink-500 sticky left-0 bg-white px-3 py-2 text-left text-xs font-semibold"
                  >
                    Élève
                  </th>
                  {visible.map((p) => (
                    <th
                      key={p.id}
                      scope="col"
                      className="text-ink-500 min-w-[6.5rem] px-2 py-2 text-left text-xs font-semibold"
                    >
                      <span className="block text-[10px] uppercase">{periodOf(p)}</span>
                      <span className="text-ink-800 line-clamp-2">{p.title}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.students.map((s) => (
                  <tr key={s.id} className="border-ink-100 border-t">
                    <th
                      scope="row"
                      className="sticky left-0 bg-white px-3 py-2 text-left font-medium"
                    >
                      <Link
                        to="/tutor/students/$studentId"
                        params={{ studentId: s.id }}
                        className="text-ink-900 hover:text-primary-700 flex items-center gap-2"
                      >
                        <AvatarCircle name={s.first_name} src={s.avatar_url} size="xs" />
                        {s.first_name}
                      </Link>
                    </th>
                    {visible.map((p) => {
                      const cell = s.packs[p.id] ?? { mastered: 0, hora: 0, assigned: 0 };
                      const percent = packMasteryPercent(cell.mastered, p.card_count);
                      return (
                        <td key={p.id} className="px-2 py-1.5">
                          <span
                            className={cn(
                              'flex h-9 items-center justify-center rounded-lg text-xs font-semibold tabular-nums',
                              TONE_CLASSES[masteryTone(percent)],
                            )}
                            title={`${cell.mastered}/${p.card_count} maîtrisées · ${cell.hora} HORA · ${cell.assigned} assignées`}
                          >
                            {p.card_count === 0 ? '—' : `${cell.mastered}/${p.card_count}`}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
